/**
 * fmwvtool-templates.ts - Templatr Templates for FMwvTool
 * 
 * Pre-configured templates for common use cases and workflows.
 * Can be selected via URL: ?template=filemaker-embedded
 */

/**
 * FileMaker Embedded Template
 * Optimized for embedding in FileMaker Web Viewer
 */
export const filemakerEmbeddedTemplate = {
  id: 'filemaker-embedded',
  name: 'FileMaker Embedded',
  description: 'Optimized for FileMaker Web Viewer with hidden navigation and batch save',
  type: 'workflow' as const,
  scope: 'app' as const,
  
  config: {
    navigation: {
      hideNav: true,
      hideSidebar: true,
      showCustomizeButton: true,
    },
    
    dataSubmission: {
      saveMode: 'batch',
      submitMethod: 'script',
      fmScript: 'jsRunScript',
    },
    
    plugins: {
      outliner: {
        behavior: {
          autoSave: false,
          saveMode: 'batch',
        },
        toolbar: {
          showImportExport: true,
        }
      }
    }
  }
};

/**
 * Development Template
 * Full features enabled for development and testing
 */
export const developmentTemplate = {
  id: 'development',
  name: 'Development Mode',
  description: 'Full navigation, auto-save, and debug features enabled',
  type: 'workflow' as const,
  scope: 'app' as const,
  
  config: {
    navigation: {
      hideNav: false,
      hideSidebar: false,
      showCustomizeButton: true,
    },
    
    dataSubmission: {
      saveMode: 'auto',
      submitMethod: 'script',
      logging: {
        enabled: true,
        level: 'debug',
        showInConsole: true,
      }
    },
    
    development: {
      samplesEnabled: true,
      showDebugInfo: true,
      verboseLogging: true,
    }
  }
};

/**
 * Presentation Template
 * Clean interface for demos and presentations
 */
export const presentationTemplate = {
  id: 'presentation',
  name: 'Presentation Mode',
  description: 'Clean interface with minimal UI for demos',
  type: 'workflow' as const,
  scope: 'app' as const,
  
  config: {
    navigation: {
      hideNav: true,
      hideSidebar: true,
      showCustomizeButton: false,
    },
    
    dataSubmission: {
      saveMode: 'auto',
      submitMethod: 'script',
      logging: {
        enabled: false,
      }
    },
    
    development: {
      samplesEnabled: true,
      showDebugInfo: false,
      verboseLogging: false,
    }
  }
};

/**
 * Outliner Task Manager Template
 * Configured for task management with multi-field view
 */
export const outlinerTaskManagerTemplate = {
  id: 'outliner-task-manager',
  name: 'Task Manager',
  description: 'Multi-field outliner configured for task management',
  type: 'plugin-config' as const,
  scope: 'plugin' as const,
  plugin: 'outliner',
  
  config: {
    appearance: {
      defaultView: 'multi',
    },
    
    multiField: {
      defaultFields: [
        { id: 'task', label: 'Task', type: 'text', width: 400 },
        { id: 'assignee', label: 'Assignee', type: 'text', width: 150 },
        { id: 'status', label: 'Status', type: 'select', width: 120, 
          options: ['Not Started', 'In Progress', 'Done', 'Blocked'] },
        { id: 'priority', label: 'Priority', type: 'select', width: 100, 
          options: ['Low', 'Medium', 'High', 'Critical'] },
        { id: 'dueDate', label: 'Due Date', type: 'date', width: 150 },
        { id: 'notes', label: 'Notes', type: 'text', width: 300 },
      ],
    },
    
    behavior: {
      autoSave: false,
      saveMode: 'batch',
    }
  }
};

/**
 * Outliner Simple Notes Template
 * Single-field outliner for simple note-taking
 */
export const outlinerSimpleNotesTemplate = {
  id: 'outliner-simple-notes',
  name: 'Simple Notes',
  description: 'Single-field outliner for quick note-taking',
  type: 'plugin-config' as const,
  scope: 'plugin' as const,
  plugin: 'outliner',
  
  config: {
    appearance: {
      defaultView: 'single',
      fontSize: '16px',
    },
    
    behavior: {
      autoSave: true,
      saveMode: 'auto',
      expandOnLoad: true,
    },
    
    toolbar: {
      showFieldViewToggle: false,
      showImportExport: false,
    }
  }
};

/**
 * oData REST API Template
 * Configured for oData REST API submission
 */
export const odataRestTemplate = {
  id: 'odata-rest',
  name: 'oData REST API',
  description: 'Configured for oData REST API data submission',
  type: 'workflow' as const,
  scope: 'app' as const,
  
  config: {
    dataSubmission: {
      saveMode: 'batch',
      submitMethod: 'odata',
      odata: {
        defaultEndpoint: 'https://api.example.com/odata',
        authType: 'basic',
        timeout: 10000,
      }
    }
  }
};

/**
 * FileMaker Data API Template
 * Configured for FileMaker Data API submission
 */
export const filemakerDataApiTemplate = {
  id: 'filemaker-dapi',
  name: 'FileMaker Data API',
  description: 'Configured for FileMaker Data API submission',
  type: 'workflow' as const,
  scope: 'app' as const,
  
  config: {
    dataSubmission: {
      saveMode: 'batch',
      submitMethod: 'dapi',
      dapi: {
        defaultEndpoint: 'https://server.example.com/fmi/data/v1',
        authType: 'token',
        timeout: 10000,
      }
    }
  }
};

// Export all templates
export const fmwvtoolTemplates = [
  filemakerEmbeddedTemplate,
  developmentTemplate,
  presentationTemplate,
  outlinerTaskManagerTemplate,
  outlinerSimpleNotesTemplate,
  odataRestTemplate,
  filemakerDataApiTemplate,
];

/**
 * Register FMwvTool templates with a Templatr instance
 * 
 * NOTE: These are actually skin/config templates, not Templatr templates.
 * Templatr templates require 'content' and 'context' fields.
 * These configs are applied via Skinnr, not Templatr.
 */
export const registerFMwvToolTemplates = (templatr: any) => {
  // TODO: Convert these to proper Templatr templates if needed
  // For now, these are handled by Skinnr as skin configurations
  console.log('[Templates] Skipping registration - these are skin configs, not Templatr templates');
  // fmwvtoolTemplates.forEach(template => {
  //   templatr.createTemplate(template);
  // });
};

/**
 * Get template by ID
 */
export const getTemplate = (id: string) => {
  return fmwvtoolTemplates.find(t => t.id === id);
};

/**
 * Get templates by type
 */
export const getTemplatesByType = (type: string) => {
  return fmwvtoolTemplates.filter(t => t.type === type);
};

/**
 * Get templates by scope
 */
export const getTemplatesByScope = (scope: string) => {
  return fmwvtoolTemplates.filter(t => t.scope === scope);
};

export default fmwvtoolTemplates;
